import { Card, CardContent } from "@/components/ui/card";
import { BriefcaseBusiness, FileText, MessageSquareQuote, Sparkles } from "lucide-react";

const steps = [
  {
    step: "01",
    title: "Load in your story",
    description:
      "Drop in your resume, experience, and skills so the coaching starts from what you've actually done.",
    icon: FileText,
    accent: "from-sky-500 to-cyan-400",
  },
  {
    step: "02",
    title: "Aim at a real role",
    description:
      "Add the job you want and we tailor the prep, talking points, and pressure points around that specific target.",
    icon: BriefcaseBusiness,
    accent: "from-emerald-500 to-teal-400",
  },
  {
    step: "03",
    title: "Rehearse the moments that matter",
    description:
      "Practice interviews, sharpen your pitch, and prep for negotiation before the real conversation shows up.",
    icon: MessageSquareQuote,
    accent: "from-amber-500 to-orange-400",
  },
];

const LandingHowItWorks = () => {
  return (
    <section className="bg-[linear-gradient(180deg,#f7fbff_0%,#ffffff_100%)] py-20">
      <div className="container mx-auto px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
            How It Works
          </p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            Built for the way a real job search actually unfolds
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600">
            Broad early, sharper when interviews hit, and still useful when the money conversation starts.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="grid gap-5">
            {steps.map((step) => {
              const Icon = step.icon;

              return (
                <Card key={step.step} className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
                  <CardContent className="flex gap-4 p-6 md:gap-6 md:p-8">
                    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${step.accent} text-white shadow-lg`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                        Step {step.step}
                      </p>
                      <h3 className="mt-2 text-2xl font-bold text-slate-900">{step.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600 md:text-base">
                        {step.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-[#243247] p-8 text-white shadow-xl shadow-slate-200">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-100">
              <Sparkles className="h-4 w-4" />
              What You Actually Get
            </div>
            <h3 className="mt-6 text-3xl font-bold leading-tight">
              Not a pep talk. Actual prep.
            </h3>
            <div className="mt-6 space-y-4 text-sm leading-7 text-slate-200 md:text-base">
              <p>Role-specific interview practice.</p>
              <p>Help telling your story without sounding rehearsed.</p>
              <p>One place to keep the whole search from turning into chaos.</p>
              <p>Negotiation prep for when the offer finally lands.</p>
            </div>
            <div className="mt-8 rounded-3xl bg-white/10 p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-100">
                The Point
              </p>
              <p className="mt-3 text-base leading-8 text-white">
                You already did the hard part of building the experience. This helps you present it clearly when it counts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingHowItWorks;
